import type { RunnitNode } from '@run-diffusion/shared';
import { cloneDeep } from 'lodash';
import { db } from '@/firebase';

export interface NodeLoaderIssue {
	code: string;
	message?: string;
}

export async function fetchResolvedRunnitNodesFromRunnit (
	runnitId: string,
): Promise<{ nodes: RunnitNode[]; issues: NodeLoaderIssue[] }> {
	const issues: NodeLoaderIssue[] = [];

	const nodesSnapshot = await db
		.collection(`runnits/${runnitId}/runnitNodes`)
		.where('deletedAt', '==', null)
		.orderBy('sortOrder', 'asc')
		.limit(100)
		.get();

	const nodes = await Promise.all(nodesSnapshot.docs.map(async (doc) => {
		const nodeData = doc.data();
		let resolvedNodeDef = nodeData.nodeDef as unknown;

		if (resolvedNodeDef && typeof (resolvedNodeDef as { get?: () => Promise<unknown> }).get === 'function') {
			try {
				const nodeDefDoc = await (resolvedNodeDef as { get: () => Promise<{ id: string; data: () => Record<string, unknown> }> }).get();
				resolvedNodeDef = {
					id: nodeDefDoc.id,
					...nodeDefDoc.data(),
				};
			} catch (err) {
				console.error('Error resolving nodeDef reference:', err);
				issues.push({ code: 'node-def-ref-error', message: String(err) });
				if (nodeData.nodeDefId) {
					try {
						const nodeDefRef = db.doc(`runnitNodeDefs/${nodeData.nodeDefId}`);
						const nodeDefDoc = await nodeDefRef.get();
						resolvedNodeDef = {
							id: nodeDefDoc.id,
							...nodeDefDoc.data(),
						};
					} catch (err2) {
						console.error('Error fetching nodeDef by ID:', err2);
						issues.push({ code: 'node-def-id-fallback-error', message: String(err2) });
					}
				}
			}
		}

		const def = resolvedNodeDef as Record<string, unknown> | null | undefined;
		if (def && def.avatarId) {
			const avatar = def.avatar as Record<string, unknown> | null | undefined;
			if (
				def.avatarId &&
				(!avatar || !avatar.id || (typeof avatar.get === 'function' || avatar.firestore))
			) {
				try {
					const avatarDoc = await db.doc(`avatars/${def.avatarId as string}`).get();
					resolvedNodeDef = {
						...def,
						avatar: avatarDoc.exists ? { ...avatarDoc.data(), id: def.avatarId } : null,
					};
				} catch (err) {
					console.error('Error fetching avatar:', err);
					issues.push({ code: 'avatar-fetch-error', message: String(err) });
					resolvedNodeDef = {
						...def,
						avatar: null,
					};
				}
			}
		}

		return {
			...nodeData,
			id: doc.id,
			nodeDef: resolvedNodeDef,
		} as RunnitNode;
	}));

	return { nodes, issues };
}

export function applyTemplateNodeCloneStrategy (
	nodes: RunnitNode[],
	isEditMode: boolean,
	newId: () => string,
): { nodes: RunnitNode[]; issues: NodeLoaderIssue[] } {
	const issues: NodeLoaderIssue[] = [];

	if (!nodes?.length) {
		return { nodes: [], issues };
	}

	const nodesWithValidIds = nodes.filter(n => n && n.id);
	if (nodesWithValidIds.length !== nodes.length) {
		console.error('loadCurrentRunnitNodes: Some nodes missing IDs', {
			total: nodes.length,
			withIds: nodesWithValidIds.length,
		});
		issues.push({
			code: 'nodes-missing-ids',
			message: `${nodesWithValidIds.length}/${nodes.length} nodes have ids`,
		});
	}

	if (isEditMode) {
		return { nodes: cloneDeep(nodesWithValidIds), issues };
	}

	return {
		nodes: cloneDeep(nodesWithValidIds).map((node: RunnitNode) => ({
			...node,
			id: newId(),
			originalNodeId: node.id,
		})),
		issues,
	};
}
